/**
 * Physics-based visualizations: Chladni-plate cymatics, magnetic field lines, Kepler orbits with
 * light trails, and a plasma ball. (The simulations that need state between frames, such as the
 * ripple tank, fluid, reaction–diffusion and double pendulum, are ISF shaders in src/content/isf.)
 */
import type { VisualizationDef } from '../../../shared/viz';

export const cymatics: VisualizationDef = {
  id: 'cymatics',
  name: 'Cymatics',
  description:
    'Sand on a vibrating plate settles on the nodal lines of the plate’s modes. Each frequency band drives one mode, so the pattern is shaped by the sound itself.',
  tags: ['energy:mid', 'style:physics'],
  role: 'background',
  params: [
    {
      id: 'plate',
      label: 'Plate',
      type: 'float',
      min: 0,
      max: 1,
      default: 0,
      step: 1,
      control: 'select',
      options: ['Square', 'Round'],
    },
    { id: 'sharp', label: 'Sharpness', type: 'float', min: 4, max: 40, default: 16 },
    { id: 'complexity', label: 'Complexity', type: 'float', min: 0.5, max: 2, default: 1 },
    { id: 'colour', label: 'Palette colour', type: 'float', min: 0, max: 1, default: 0.5 },
  ],
  fragment: /* glsl */ `
// Chladni mode (n, m) of a square plate, or an approximate mode of a round plate.
float mode(vec2 p, float n, float m, bool round) {
  if (round) {
    float r = length(p), a = atan(p.y, p.x);
    return cos(n * PI * r * 1.3) * cos(m * a);
  }
  return cos(n * PI * p.x) * cos(m * PI * p.y) - cos(m * PI * p.x) * cos(n * PI * p.y);
}
void main() {
  vec2 p = (gl_FragCoord.xy - 0.5 * uResolution) / uResolution.y * 2.0;
  bool round = uP[0] > 0.5;
  float plate = round ? smoothstep(1.0, 0.98, length(p)) : step(abs(p.x), 1.0) * step(abs(p.y), 1.0);
  float f = 0.0, total = 0.0;
  for (int i = 0; i < 8; i++) {
    float fi = float(i);
    float n = (1.0 + floor(fi * 0.5)) * uP[2];
    float m = (2.0 + fi) * uP[2];
    float w = uBands[i] * uBands[i] + 0.002;
    f += w * mode(p, n, m, round);
    total += w;
  }
  f /= total;
  // Sand gathers where the plate doesn't move; a beat makes it jump and blur.
  float sharp = uP[1] * (1.0 - 0.5 * pulse());
  float sand = exp(-abs(f) * sharp);
  float grain = 0.6 + 0.4 * hash21(floor(gl_FragCoord.xy));
  vec3 metal = mix(vec3(0.05, 0.05, 0.06), palette(0.1) * 0.15, uP[3]) * (0.8 + 0.2 * p.y);
  vec3 sandCol = mix(vec3(0.95, 0.88, 0.7), palette(0.6 + f * 0.3), uP[3]);
  vec3 col = mix(metal, sandCol * grain * (0.8 + 0.4 * uLevel), sand) * plate;
  fragColor = vec4(col, 1.0);
}`,
};

export const fieldLines: VisualizationDef = {
  id: 'field-lines',
  name: 'Field Lines',
  description:
    'Magnetic field lines between drifting poles; each pole glows with its frequency band.',
  tags: ['energy:mid', 'style:physics'],
  role: 'element',
  params: [
    { id: 'lines', label: 'Lines', type: 'float', min: 4, max: 40, default: 16, step: 1 },
    { id: 'poles', label: 'Pole pairs', type: 'float', min: 1, max: 4, default: 2, step: 1 },
    { id: 'drift', label: 'Drift', type: 'float', min: 0, max: 2, default: 1 },
    { id: 'width', label: 'Line width', type: 'float', min: 0.5, max: 3, default: 1.2 },
  ],
  fragment: /* glsl */ `
void main() {
  vec2 p = (gl_FragCoord.xy - 0.5 * uResolution) / uResolution.y;
  float pairs = floor(uP[1]);
  // The stream function of 2D point charges: field lines are its level sets. With unit charges
  // and a whole number of lines, its jumps across branch cuts are whole multiples: no seams.
  float psi = 0.0;
  vec2 field = vec2(0.0);
  vec3 glow = vec3(0.0);
  for (int i = 0; i < 8; i++) {
    float fi = float(i);
    if (fi >= pairs * 2.0) break;
    float q = mod(fi, 2.0) < 0.5 ? 1.0 : -1.0;
    float t = uTime * 0.15 * uP[2];
    vec2 c = 0.45 * vec2(sin(t * (0.7 + fi * 0.13) + fi * 2.1), cos(t * (0.5 + fi * 0.17) + fi * 1.3));
    c.x *= uResolution.x / uResolution.y;
    vec2 d = p - c;
    psi += q * atan(d.y, d.x);
    field += q * d / max(dot(d, d), 1e-4);
    float band = uBands[i];
    glow += palette(fi / 8.0) * (0.004 / (dot(d, d) + 0.0004)) * (0.3 + band);
  }
  float lines = floor(uP[0]);
  float v = psi * lines / TAU;
  float dist = abs(fract(v + 0.5) - 0.5) / max(fwidth(v), 1e-4);
  float line = smoothstep(uP[3], 0.0, dist);
  float strength = clamp(length(field) * 0.05, 0.0, 1.0);
  vec3 col = palette(0.5 + 0.3 * strength + uBarPhase * 0.2) * line * (0.25 + 0.75 * strength) * (0.8 + 0.4 * pulse());
  col += glow * 0.25;
  fragColor = vec4(col, 1.0);
}`,
};

export const gravityOrbits: VisualizationDef = {
  id: 'gravity-orbits',
  name: 'Gravity Orbits',
  description:
    'Bodies on Kepler orbits around an unseen mass, trailing light. They speed up at the close approach, just as real orbits do; each one shines with its band.',
  tags: ['energy:mid', 'style:physics'],
  role: 'element',
  params: [
    { id: 'bodies', label: 'Bodies', type: 'float', min: 3, max: 24, default: 14, step: 1 },
    { id: 'speed', label: 'Speed', type: 'float', min: 0, max: 3, default: 1 },
    { id: 'trail', label: 'Trail length', type: 'float', min: 0.2, max: 4, default: 1.5 },
    { id: 'ecc', label: 'Eccentricity', type: 'float', min: 0, max: 0.9, default: 0.55 },
  ],
  fragment: /* glsl */ `
void main() {
  vec2 p = (gl_FragCoord.xy - 0.5 * uResolution) / uResolution.y;
  vec3 col = vec3(0.0);
  float rp = length(p);
  float thp = atan(p.y, p.x);
  float n = floor(uP[0]);
  for (int i = 0; i < 24; i++) {
    float fi = float(i);
    if (fi >= n) break;
    float a = 0.12 + 0.3 * hash21(vec2(fi, 1.0));
    float e = uP[3] * hash21(vec2(fi, 2.0));
    float w = hash21(vec2(fi, 3.0)) * TAU;           // orientation of the ellipse
    // Mean anomaly (Kepler's third law) → eccentric anomaly (Newton) → true anomaly.
    float M = hash21(vec2(fi, 4.0)) * TAU + uTime * uP[1] * 0.25 / pow(a / 0.12, 1.5);
    float E = M;
    for (int k = 0; k < 5; k++) E -= (E - e * sin(E) - M) / (1.0 - e * cos(E));
    float nu = 2.0 * atan(sqrt(1.0 + e) * sin(E * 0.5), sqrt(1.0 - e) * cos(E * 0.5));
    float l = a * (1.0 - e * e);
    // Distance from this pixel to the orbit, measured along the radius at the pixel's angle.
    float rOrbit = l / (1.0 + e * cos(thp - w));
    float d = abs(rp - rOrbit);
    // How far behind the body this point of the orbit is (in angle, in the direction of travel).
    float behind = mod(nu - (thp - w), TAU);
    float band = uBands[int(mod(fi, 8.0))];
    vec3 c = palette(fi / n + uBarPhase * 0.1);
    float trail = exp(-behind / (uP[2] * 0.5)) * smoothstep(0.004, 0.0, d);
    col += c * trail * (0.4 + band);
    vec2 body = (l / (1.0 + e * cos(nu))) * vec2(cos(nu + w), sin(nu + w));
    float bd = length(p - body);
    col += c * (smoothstep(0.008, 0.0, bd) * 2.0 + 0.002 / (bd * bd + 0.0004) * 0.02) * (0.6 + band + 0.5 * pulse());
  }
  col += palette(0.95) * 0.0015 / (rp * rp + 0.001);
  fragColor = vec4(col, 1.0);
}`,
};

export const plasmaBall: VisualizationDef = {
  id: 'plasma-ball',
  name: 'Plasma Ball',
  description:
    'A Tesla plasma globe: arcs crackle from the electrode to the glass, more of them as the music gets louder, flaring on every hit.',
  tags: ['energy:high', 'style:physics'],
  role: 'element',
  params: [
    { id: 'size', label: 'Size', type: 'float', min: 0.5, max: 1.5, default: 1 },
    { id: 'arcs', label: 'Arcs', type: 'float', min: 2, max: 12, default: 7, step: 1 },
    { id: 'jitter', label: 'Crackle', type: 'float', min: 0, max: 2, default: 1 },
    { id: 'glass', label: 'Glass', type: 'float', min: 0, max: 1, default: 0.5 },
  ],
  fragment: /* glsl */ `
void main() {
  vec2 p = (gl_FragCoord.xy - 0.5 * uResolution) / uResolution.y;
  float R = 0.4 * uP[0];
  float r = length(p);
  float a = atan(p.y, p.x);
  vec3 col = vec3(0.0);
  float arcs = floor(uP[1] * (0.5 + 0.7 * uLevel) + 1.0);
  if (r < R) {
    float t = r / R;
    for (int k = 0; k < 12; k++) {
      float fk = float(k);
      if (fk >= arcs) break;
      float base = hash21(vec2(fk, 5.0)) * TAU + (noise2(vec2(fk * 3.1, uTime * 0.25)) - 0.5) * 4.0;
      float wob = (fbm(vec2(t * 6.0 + fk * 9.0, uTime * 3.0)) - 0.5) * 1.2 * uP[2] * t;
      float ang = base + wob;
      float da = abs(mod(a - ang + PI, TAU) - PI) * r;
      float w = 0.0025 + 0.004 * t;
      float bright = (0.6 + uOnset * 1.5 + 0.5 * pulse()) * (0.4 + 0.6 * uBands[int(mod(fk, 8.0))] * 2.0);
      col += palette(0.6 + t * 0.35) * (smoothstep(w, 0.0, da) + 0.35 * exp(-da / (w * 6.0))) * bright;
      // A branch that splits off part-way.
      float bw = (fbm(vec2(t * 9.0 + fk * 4.0, uTime * 4.0)) - 0.5) * 2.0 * uP[2] * smoothstep(0.4, 1.0, t);
      float db = abs(mod(a - ang - bw + PI, TAU) - PI) * r;
      col += palette(0.7 + t * 0.3) * smoothstep(w * 0.7, 0.0, db) * bright * 0.6 * step(0.45, t);
    }
    // Where the arcs touch the glass, a hot spot.
    col += palette(0.75) * 0.03 / (abs(r - R) + 0.01) * 0.2;
  }
  // Electrode and glass.
  col += palette(0.5) * (exp(-r * 40.0) * 2.0 + 0.01 / (r + 0.01)) * (0.8 + 0.6 * uBass);
  col += vec3(0.6, 0.7, 1.0) * uP[3] * (exp(-abs(r - R) * 160.0) * 0.4 + exp(-length(p - vec2(-0.35, 0.45) * R) * 30.0) * 0.4);
  fragColor = vec4(col, 1.0);
}`,
};

export const PHYSICS_VISUALIZATIONS: VisualizationDef[] = [
  cymatics,
  fieldLines,
  gravityOrbits,
  plasmaBall,
];
